<template>
  <div
    class="pin-input"
    :class="{ 'pin-input--error': error }"
    role="group"
    :aria-label="label"
  >
    <input
      v-for="(digit, i) in digits"
      :key="i"
      :ref="el => setInputRef(el, i)"
      class="pin-input--box"
      type="text"
      inputmode="numeric"
      :autocomplete="i === 0 ? 'one-time-code' : 'off'"
      :aria-label="`${label} ${i + 1} / ${length}`"
      :value="digit"
      :disabled="disabled"
      @input="onInput(i, $event)"
      @keydown="onKeydown(i, $event)"
    >
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'

defineOptions({ name: 'PinInput' })
const props = defineProps({
  label: { type: String, default: 'Verification code' },
  length: { type: Number, default: 6 },
  disabled: { type: Boolean, default: false },
  error: { type: Boolean, default: false },
  autofocus: { type: Boolean, default: true }
})

const model = defineModel({ type: String, default: '' })

const digits = computed(() => Array.from({ length: props.length }, (_, i) => model.value[i] ?? ''))

const inputRefs = []
const setInputRef = (el, i) => {
  inputRefs[i] = el
}
const focusInput = i => inputRefs[Math.max(0, Math.min(i, props.length - 1))]?.focus()

const onInput = (i, e) => {
  const raw = e.target.value.replace(/\D/g, '')
  const chars = digits.value.slice()

  if (raw.length > 1) {
    // A multi-character value lands here on paste or OS autofill (e.g. iOS's
    // one-time-code suggestion), which fills the whole code into one box.
    for (let j = 0; j < raw.length && i + j < props.length; j++) chars[i + j] = raw[j]
    model.value = chars.join('')
    focusInput(i + raw.length)
    return
  }

  chars[i] = raw
  model.value = chars.join('')
  if (raw) focusInput(i + 1)
}

const onKeydown = (i, e) => {
  if (e.key === 'Backspace' && !digits.value[i]) {
    e.preventDefault()
    const chars = digits.value.slice()
    chars[i - 1] = ''
    model.value = chars.join('')
    focusInput(i - 1)
  } else if (e.key === 'ArrowLeft') {
    e.preventDefault()
    focusInput(i - 1)
  } else if (e.key === 'ArrowRight') {
    e.preventDefault()
    focusInput(i + 1)
  }
}

onMounted(() => {
  if (props.autofocus) focusInput(0)
})
</script>

<style scoped>
@reference "#app-styles.css";

.pin-input {
  @apply flex gap-2;
}

.pin-input--box {
  @apply w-8 h-10 rounded-md;
  @apply font-mono font-medium text-center text-xl ink-strong;
  @apply bg-transparent border border-edge-float outline-none transition-colors;

  &:focus { @apply border-primary; }
  &:disabled { @apply opacity-50 cursor-not-allowed; }
}

.pin-input--error .pin-input--box {
  @apply border-negative;
}
</style>
